/**
 * Truy cập dữ liệu của module `rpa` (Quản lý RPA, ADR-0103) qua Gateway: `/api/v1/m/rpa/...`. Đường con giữ như
 * rpa-server của native, bỏ tiền tố `/api/rpa` và `/runtime`; tenant lấy từ token, không gửi tham số tổ chức.
 *
 * **Chỉ API thật, không có máy chủ giả.** Project, Workflow, sơ đồ bước và biến của Workflow nằm ở OpenCore của
 * chính tenant (địa chỉ và JWT do tenant khai ở tab Kết nối OpenCore) — dựng một tầng mock là tạo ra nguồn sự thật
 * thứ hai phải giữ khớp với OpenCore. Soát bốn trạng thái của các màn RPA phải chạy module thật: tenant chưa khai kết
 * nối cho ra trạng thái `RPA_OPENCORE_NOT_CONFIGURED`, OpenCore rỗng cho ra trạng thái rỗng.
 *
 * Lỗi là Problem Details (`lib/api/http.ts` chuẩn hoá): `detail` là câu tiếng Việt của máy chủ, màn hiện nguyên văn.
 */
import { goiApi, goiApiTep } from '../api/http';
import type {
  BanLuuGiaTri,
  HanhDongAgent,
  KenhCauHinh,
  KenhThongBao,
  QuyTacThongBao,
  TaiKhoanGui,
  TaskAgent,
  ThanQuyTacThongBao,
  ThanTaiKhoanGui,
  ThanTaskAgent,
  TrangThaiAgent,
  XemTruocHanhDong,
  DanhSachInstanceTongQuan,
  DanhSachLicense,
  DanhSachUserOpenCore,
  KetQuaDongBoLicense,
  KetQuaResetThietBi,
  LicenseRpa,
  QuyenOpenCoreLicense,
  RoleOpenCore,
  ThanCapLicense,
  ThanRoleOpenCore,
  ThanSuaLicense,
  ThanUserOpenCore,
  TongQuanRpa,
  UserOpenCore,
  BaoCaoLoi,
  ChiTietLoi,
  CodeLoiDayDu,
  DanhSachSuCo,
  DoiChieuDuLieu,
  DongThoiGianLuot,
  GiaTriWorkflow,
  KetNoi,
  KetQuaCao,
  KetQuaLuuGiaTri,
  KhoaLuotDuLieu,
  LienKetOpenCore,
  LoaiDuLieu,
  LogRobot,
  LuotChay,
  LuotGanNhat,
  MucCho,
  ProjectOpenRpa,
  SoDoWorkflow,
  ThanChiTietLoi,
  ThanCodeLoi,
  ThanSuaKetNoi,
  ThongKeDuLieu,
  TongQuanCodeLoi,
  TrangDuLieu,
  TrangLichSu,
  TrangThaiCho,
  TrangThaiOpenCore,
  TruyVanLichSu,
  TuyChonChay,
  WorkflowOpenCore,
} from '../../modules/rpa/types';

export const GOC_RPA = '/api/v1/m/rpa';

const ma = (id: string) => encodeURIComponent(id);

export const rpaRepository = {
  // ─── Cấu hình lỗi ───

  tongQuanCode: (): Promise<TongQuanCodeLoi> => goiApi(`${GOC_RPA}/error-codes`),

  code: (code: string): Promise<CodeLoiDayDu> => goiApi(`${GOC_RPA}/error-codes/${ma(code)}`),

  /** Có `pendingId` thì exception chờ đó thành dòng chi tiết đầu tiên của code mới và rời danh sách chờ. */
  taoCode: (than: ThanCodeLoi & { pendingId?: string }): Promise<CodeLoiDayDu> =>
    goiApi(`${GOC_RPA}/error-codes`, { method: 'POST', body: than }),

  suaCode: (code: string, than: Partial<ThanCodeLoi>): Promise<CodeLoiDayDu> =>
    goiApi(`${GOC_RPA}/error-codes/${ma(code)}`, { method: 'PATCH', body: than }),

  /** `409 RPA_ERROR_CODE_HAS_DETAILS` khi code còn chi tiết. */
  xoaCode: (code: string): Promise<void> => goiApi(`${GOC_RPA}/error-codes/${ma(code)}`, { method: 'DELETE' }),

  /** `409 RPA_ERROR_DETAIL_EXISTS` khi exception + thông điệp đã thuộc một code. */
  themChiTiet: (code: string, than: ThanChiTietLoi): Promise<ChiTietLoi> =>
    goiApi(`${GOC_RPA}/error-codes/${ma(code)}/details`, { method: 'POST', body: than }),

  suaChiTiet: (id: string, than: Partial<ThanChiTietLoi>): Promise<ChiTietLoi> =>
    goiApi(`${GOC_RPA}/error-details/${ma(id)}`, { method: 'PATCH', body: than }),

  xoaChiTiet: (id: string): Promise<void> => goiApi(`${GOC_RPA}/error-details/${ma(id)}`, { method: 'DELETE' }),

  danhSachCho: (status: TrangThaiCho): Promise<MucCho[]> => goiApi(`${GOC_RPA}/error-pending`, { query: { status } }),

  ganCho: (id: string, code: string): Promise<CodeLoiDayDu> =>
    goiApi(`${GOC_RPA}/error-pending/${ma(id)}/assign`, { method: 'POST', body: { code } }),

  boQuaCho: (id: string): Promise<MucCho> => goiApi(`${GOC_RPA}/error-pending/${ma(id)}/ignore`, { method: 'POST' }),

  phanLoai: (): Promise<string[]> => goiApi(`${GOC_RPA}/error-classifications`),

  // ─── Kết nối OpenCore của tenant ───

  ketNoi: (): Promise<KetNoi> => goiApi(`${GOC_RPA}/connection`),

  luuKetNoi: (than: ThanSuaKetNoi): Promise<KetNoi> => goiApi(`${GOC_RPA}/connection`, { method: 'PUT', body: than }),

  trangThaiOpenCore: (): Promise<TrangThaiOpenCore> => goiApi(`${GOC_RPA}/opencore/status`),

  // ─── Đọc OpenCore ───

  /** `fresh` bỏ bộ nhớ đệm 30 giây của máy chủ. */
  projects: (fresh = false): Promise<ProjectOpenRpa[]> =>
    goiApi(`${GOC_RPA}/projects`, { query: fresh ? { fresh: '1' } : {} }),

  workflows: (): Promise<WorkflowOpenCore[]> => goiApi(`${GOC_RPA}/workflows`),

  soDo: (id: string): Promise<SoDoWorkflow> => goiApi(`${GOC_RPA}/workflows/${ma(id)}/diagram`),

  giaTri: (id: string): Promise<GiaTriWorkflow> => goiApi(`${GOC_RPA}/workflows/${ma(id)}/values`),

  /** `409 RPA_WORKFLOW_CHANGED` khi `baseVersion` không còn là bản mới nhất trên OpenCore. */
  luuGiaTri: (
    id: string,
    than: { baseVersion: number | null; changes: { key: string; value: string }[] },
  ): Promise<KetQuaLuuGiaTri> => goiApi(`${GOC_RPA}/workflows/${ma(id)}/values`, { method: 'PUT', body: than }),

  lichSuGiaTri: (id: string): Promise<BanLuuGiaTri[]> => goiApi(`${GOC_RPA}/workflows/${ma(id)}/values/history`),

  khoiPhucGiaTri: (id: string, than: { backupId: string; baseVersion: number | null }): Promise<GiaTriWorkflow> =>
    goiApi(`${GOC_RPA}/workflows/${ma(id)}/values/restore`, { method: 'POST', body: than }),

  // ─── Tài khoản OpenCore của người dùng, chạy Workflow ───

  lienKet: (): Promise<LienKetOpenCore> => goiApi(`${GOC_RPA}/opencore-link`),

  /** `401 RPA_OPENCORE_INVALID_CREDENTIALS`, `429 RPA_OPENCORE_LINK_LOCKED` sau 5 lần sai. */
  moLienKet: (than: { username: string; password: string }): Promise<LienKetOpenCore> =>
    goiApi(`${GOC_RPA}/opencore-link`, { method: 'POST', body: than }),

  ngatLienKet: (): Promise<LienKetOpenCore> => goiApi(`${GOC_RPA}/opencore-link`, { method: 'DELETE' }),

  tuyChonChay: (id: string): Promise<TuyChonChay> => goiApi(`${GOC_RPA}/workflows/${ma(id)}/run`),

  batDauChay: (id: string, than: { projectId: string; inputData: Record<string, unknown> }): Promise<LuotChay> =>
    goiApi(`${GOC_RPA}/workflows/${ma(id)}/run`, { method: 'POST', body: than }),

  luotGanNhat: (id: string): Promise<LuotGanNhat> => goiApi(`${GOC_RPA}/workflows/${ma(id)}/run/latest`),

  // ─── Lịch sử lượt chạy, báo cáo lỗi, log Robot ───

  /** Máy chủ đồng bộ OpenCore trước khi đọc (chờ tối đa 8 giây); OpenCore lỗi thì vẫn trả lịch sử đã lưu + `syncError`. */
  lichSu: (q: TruyVanLichSu): Promise<TrangLichSu> =>
    goiApi(`${GOC_RPA}/history`, { query: { page: q.page, pageSize: q.pageSize, search: q.search, status: q.status } }),

  /** `null` khi Robot chưa gửi báo cáo lỗi cho lượt này. */
  baoCaoInstance: (instanceId: string): Promise<BaoCaoLoi | null> =>
    goiApi(`${GOC_RPA}/instances/${ma(instanceId)}/error-report`),

  baoCaoLuot: (runCode: string): Promise<BaoCaoLoi | null> => goiApi(`${GOC_RPA}/runs/${ma(runCode)}/error-report`),

  /** Ảnh, video, log bằng chứng: thẻ `<img>`/`<video>` không gắn được token, nên tải về thành `Blob`. */
  tepBangChung: (executionId: string, fileId: string): Promise<Blob> =>
    goiApiTep(`${GOC_RPA}/error-reports/${ma(executionId)}/files/${ma(fileId)}`, '*/*'),

  /** `null` khi Robot chưa gửi log cho lượt này. */
  logInstance: (instanceId: string): Promise<LogRobot | null> => goiApi(`${GOC_RPA}/instances/${ma(instanceId)}/log`),

  /** Mốc Native của một lượt `RUN-…` kèm instance OpenCore gần thời điểm bắt đầu nhất. */
  dongThoiGian: (runCode: string): Promise<DongThoiGianLuot> => goiApi(`${GOC_RPA}/runs/${ma(runCode)}/logs`),

  // ─── Dữ liệu thực thi ───

  thongKeDuLieu: (k: KhoaLuotDuLieu): Promise<ThongKeDuLieu> =>
    goiApi(`${GOC_RPA}/execution-data/stats`, { query: { instanceId: k.instanceId, runId: k.runId } }),

  /** `all` trả mọi dòng khớp (xuất tệp), bỏ phân trang. */
  dongDuLieu: (
    q: KhoaLuotDuLieu & { kind: LoaiDuLieu; search: string; page: number; pageSize: number; all?: boolean },
  ): Promise<TrangDuLieu> =>
    goiApi(`${GOC_RPA}/execution-data/rows`, {
      query: {
        instanceId: q.instanceId,
        runId: q.runId,
        kind: q.kind,
        search: q.search,
        page: q.page,
        pageSize: q.pageSize,
        all: q.all ? '1' : undefined,
      },
    }),

  doiChieu: (k: KhoaLuotDuLieu, keys: readonly string[]): Promise<DoiChieuDuLieu> =>
    goiApi(`${GOC_RPA}/execution-data/compare`, { query: { instanceId: k.instanceId, runId: k.runId, keys: keys.join(',') } }),

  ketQuaCao: (q: { executionId: string; tag?: string }): Promise<KetQuaCao[]> =>
    goiApi(`${GOC_RPA}/scrape-results`, { query: { executionId: q.executionId, tag: q.tag, limit: 500 } }),

  // ─── Sự cố ───

  /** 30 ngày, tối đa 500 dòng mỗi nguồn; OpenCore lỗi thì vẫn có hai nguồn còn lại kèm `meta.opencoreError`. */
  suCo: (): Promise<DanhSachSuCo> => goiApi(`${GOC_RPA}/incidents`),

  // ─── Dashboard RPA ───

  /** Instance OpenRPA `days` ngày gần nhất (cửa sổ trượt), tối đa 5.000; OpenCore lỗi là `502 RPA_OPENCORE_HISTORY_UNAVAILABLE`. */
  tongQuan: (q: { days: number; projectId: string }): Promise<TongQuanRpa> =>
    goiApi(`${GOC_RPA}/dashboard`, { query: { days: q.days, projectId: q.projectId } }),

  /** Bảng lượt chạy dưới biểu đồ: 200 instance mới nhất kèm tóm tắt báo cáo lỗi và log Robot. */
  instancesTongQuan: (q: { days: number; projectId: string }): Promise<DanhSachInstanceTongQuan> =>
    goiApi(`${GOC_RPA}/dashboard/instances`, { query: { days: q.days, projectId: q.projectId } }),

  // ─── License RPA ───

  licenses: (): Promise<DanhSachLicense> => goiApi(`${GOC_RPA}/licenses`),

  /** `409 RPA_LICENSE_USER_PROJECT_EXISTS` khi User đã có License chưa thu hồi trong Project. */
  capLicense: (than: ThanCapLicense): Promise<LicenseRpa> => goiApi(`${GOC_RPA}/licenses`, { method: 'POST', body: than }),

  /** Mở `active` khi đã hết hạn là `403 RPA_LICENSE_EXPIRED`; gia hạn License hết hạn thì máy chủ tự mở lại. */
  suaLicense: (id: string, than: ThanSuaLicense): Promise<LicenseRpa> =>
    goiApi(`${GOC_RPA}/licenses/${ma(id)}`, { method: 'PATCH', body: than }),

  doiTrangThaiLicense: (id: string, status: 'active' | 'locked' | 'revoked'): Promise<LicenseRpa> =>
    goiApi(`${GOC_RPA}/licenses/${ma(id)}/status`, { method: 'PATCH', body: { status } }),

  /** Gỡ bind mọi máy đang active; bản ghi thiết bị giữ làm lịch sử. */
  resetThietBi: (id: string): Promise<KetQuaResetThietBi> =>
    goiApi(`${GOC_RPA}/licenses/${ma(id)}/reset-hwid`, { method: 'POST' }),

  /** `502 RPA_OPENCORE_ACCESS_SYNC_FAILED` khi OpenCore không nhận thay đổi role/ACL. */
  dongBoLicense: (id: string): Promise<KetQuaDongBoLicense> =>
    goiApi(`${GOC_RPA}/licenses/${ma(id)}/sync-opencore`, { method: 'POST' }),

  quyenLicense: (id: string): Promise<QuyenOpenCoreLicense> => goiApi(`${GOC_RPA}/licenses/${ma(id)}/opencore-access`),

  /** Gỡ quyền OpenCore trước rồi mới xoá; OpenCore lỗi thì không xoá. */
  xoaLicense: (id: string): Promise<void> => goiApi(`${GOC_RPA}/licenses/${ma(id)}`, { method: 'DELETE' }),

  // ─── User, Role OpenCore ───

  usersOpenCore: (): Promise<DanhSachUserOpenCore> => goiApi(`${GOC_RPA}/opencore/users`),

  /** `502 RPA_OPENCORE_PASSWORD_UNSUPPORTED` khi OpenCore không nhận mật khẩu (máy chủ đã huỷ tạo). */
  taoUserOpenCore: (than: ThanUserOpenCore): Promise<UserOpenCore> =>
    goiApi(`${GOC_RPA}/opencore/users`, { method: 'POST', body: than }),

  /** `409 RPA_OPENCORE_ADMIN_CONFIRM_REQUIRED` khi thêm vào `admins` mà thiếu `confirmAdmin`. */
  suaUserOpenCore: (id: string, than: ThanUserOpenCore): Promise<UserOpenCore> =>
    goiApi(`${GOC_RPA}/opencore/users/${ma(id)}`, { method: 'PUT', body: than }),

  khoaUserOpenCore: (id: string, khoa: boolean): Promise<UserOpenCore> =>
    goiApi(`${GOC_RPA}/opencore/users/${ma(id)}/${khoa ? 'disable' : 'enable'}`, { method: 'POST' }),

  /** `409 RPA_OPENCORE_USER_HAS_LICENSE`, `…_USER_PROTECTED`, `…_USER_SELF`, `…_ROLE_MANAGED`. */
  xoaUserOpenCore: (id: string): Promise<{ id: string }> => goiApi(`${GOC_RPA}/opencore/users/${ma(id)}`, { method: 'DELETE' }),

  rolesOpenCore: (): Promise<RoleOpenCore[]> => goiApi(`${GOC_RPA}/opencore/roles`),

  taoRoleOpenCore: (than: ThanRoleOpenCore): Promise<RoleOpenCore> =>
    goiApi(`${GOC_RPA}/opencore/roles`, { method: 'POST', body: than }),

  /** `409 RPA_OPENCORE_SYSTEM_ROLE` khi đổi tên role hệ thống. */
  suaRoleOpenCore: (id: string, than: ThanRoleOpenCore): Promise<RoleOpenCore> =>
    goiApi(`${GOC_RPA}/opencore/roles/${ma(id)}`, { method: 'PUT', body: than }),

  xoaRoleOpenCore: (id: string): Promise<{ id: string }> => goiApi(`${GOC_RPA}/opencore/roles/${ma(id)}`, { method: 'DELETE' }),

  // ─── Thông báo ───

  quyTacThongBao: async (): Promise<QuyTacThongBao[]> =>
    (await goiApi<{ data: QuyTacThongBao[] }>(`${GOC_RPA}/notification-rules`)).data,

  /** `400 RPA_SENDER_*` khi tài khoản gửi đã chọn không có hoặc sai loại. */
  taoQuyTac: (than: ThanQuyTacThongBao): Promise<QuyTacThongBao> =>
    goiApi(`${GOC_RPA}/notification-rules`, { method: 'POST', body: than }),

  suaQuyTac: (id: string, than: Partial<ThanQuyTacThongBao>): Promise<QuyTacThongBao> =>
    goiApi(`${GOC_RPA}/notification-rules/${ma(id)}`, { method: 'PATCH', body: than }),

  xoaQuyTac: (id: string): Promise<void> => goiApi(`${GOC_RPA}/notification-rules/${ma(id)}`, { method: 'DELETE' }),

  /** Quy tắc mọi Project: tắt ở một Project = thêm vào `excludedProjects`. Quy tắc riêng là `409 RPA_NOTIFICATION_RULE_NOT_INHERITED`. */
  batQuyTacChoProject: (id: string, projectCode: string, enabled: boolean): Promise<QuyTacThongBao> =>
    goiApi(`${GOC_RPA}/notification-rules/${ma(id)}/projects/${ma(projectCode)}`, { method: 'PUT', body: { enabled } }),

  kenhThongBao: async (): Promise<KenhCauHinh[]> =>
    (await goiApi<{ data: KenhCauHinh[] }>(`${GOC_RPA}/notification-rules/channels`)).data,

  /** Gửi tin thử tới một người nhận qua kênh; `accountId` trống = tài khoản mặc định. */
  guiThuKenh: (than: { channel: KenhThongBao; target: string; accountId: string }): Promise<{ success: true }> =>
    goiApi(`${GOC_RPA}/notification-rules/test`, { method: 'POST', body: than }),

  // ─── Tài khoản gửi ───

  taiKhoanGui: async (): Promise<TaiKhoanGui[]> => (await goiApi<{ data: TaiKhoanGui[] }>(`${GOC_RPA}/sender-accounts`)).data,

  taoTaiKhoanGui: (than: ThanTaiKhoanGui): Promise<TaiKhoanGui> =>
    goiApi(`${GOC_RPA}/sender-accounts`, { method: 'POST', body: than }),

  /** Không gửi `appPassword`/`botToken` (hoặc để trống) là giữ bí mật đã lưu; không đổi được loại. */
  suaTaiKhoanGui: (id: string, than: ThanTaiKhoanGui): Promise<TaiKhoanGui> =>
    goiApi(`${GOC_RPA}/sender-accounts/${ma(id)}`, { method: 'PUT', body: than }),

  xoaTaiKhoanGui: (id: string): Promise<void> => goiApi(`${GOC_RPA}/sender-accounts/${ma(id)}`, { method: 'DELETE' }),

  /** Gửi thử bằng dữ liệu đang nhập (chưa lưu). Gmail gửi tới `to` hoặc chính email gửi; Telegram cần `to`. */
  guiThuTaiKhoan: (than: { id?: string; account: ThanTaiKhoanGui; to?: string }): Promise<{ success: true }> =>
    goiApi(`${GOC_RPA}/sender-accounts/test`, { method: 'POST', body: than }),

  // ─── Trợ lý Agent ───

  trangThaiAgent: (): Promise<TrangThaiAgent> => goiApi(`${GOC_RPA}/agent/status`),

  /** 50 task mới nhất. */
  taskAgent: async (): Promise<TaskAgent[]> => (await goiApi<{ data: TaskAgent[] }>(`${GOC_RPA}/agent/tasks`)).data,

  /** `202`: agent xử lý ở nền. `503 RPA_AGENT_NOT_CONFIGURED`; `429 RPA_AGENT_BUSY` khi đã có 3 task đang chạy. */
  giaoTaskAgent: (than: ThanTaskAgent): Promise<TaskAgent> => goiApi(`${GOC_RPA}/agent/tasks`, { method: 'POST', body: than }),

  huyTaskAgent: (id: string): Promise<TaskAgent> => goiApi(`${GOC_RPA}/agent/tasks/${ma(id)}/cancel`, { method: 'POST' }),

  xemTruocHanhDong: (id: string, index: number): Promise<XemTruocHanhDong> =>
    goiApi(`${GOC_RPA}/agent/tasks/${ma(id)}/actions/${index}/preview`),

  /** `check_robot` không cần Project; hai loại chạy Robot thật chỉ thực thi được một lần. */
  thucThiHanhDong: (id: string, index: number, than: { projectId?: string }): Promise<HanhDongAgent> =>
    goiApi(`${GOC_RPA}/agent/tasks/${ma(id)}/actions/${index}/execute`, { method: 'POST', body: than }),
};
